import React from 'react';
import { Helmet } from 'react-helmet-async';

const udemyLink = 'https://www.udemy.com/course/big-data-hadoop-spark-project/?referralCode=E6CC33BD9128AD170C4A';

const CourseDataEng: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Data Engineering Masterclass - Top Rated Big Data Course | FutureXSkills</title>
        <meta name="description" content="Master Data Engineering with Hadoop, Spark, AWS, Databricks & Apache NiFi. Job-ready skills with hands-on projects. Top-rated course with 100K+ students!" />
        <meta name="keywords" content="data engineering course, big data course, hadoop training, spark course, AWS data engineering, databricks course, apache nifi, PySpark tutorial, scala programming, data pipeline course, ETL course, top rated data course, data engineer certification, big data masterclass, cloud data engineering" />
        <meta property="og:title" content="Data Engineering Masterclass - Expert Big Data Training" />
        <meta property="og:description" content="Become job-ready Data Engineer with Hadoop, Spark, AWS & Databricks. Real-world projects, expert instruction." />
        <meta property="og:url" content="https://futurexskills.com/courses/data-engineering" />
        <link rel="canonical" href="https://futurexskills.com/courses/data-engineering" />
      </Helmet>
      <div className="max-w-6xl mx-auto py-16 px-4">
      <div className="text-center mb-12">
        <h1 className="text-4xl md:text-5xl font-bold mb-6 gradient-text leading-relaxed pb-2 overflow-visible">
          Data Engineering Masterclass
        </h1>
        
        <h2 className="text-2xl md:text-3xl text-accent-400 font-semibold mb-8">
          🚀 Become a Job-Ready Data Engineer with Real-World Projects!
        </h2>

        <p className="text-xl text-gray-700 mb-8 max-w-3xl mx-auto leading-relaxed">
          Master the <span className="text-blue-600 font-semibold">complete modern data engineering ecosystem</span> 
          from Hadoop and Spark to AWS Glue, Databricks, and Apache NiFi.
        </p>

        <div className="mb-8">
          <a
            href={udemyLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center bg-gradient-to-r from-yellow-500 to-orange-500 text-black font-bold px-8 py-4 rounded-lg hover:from-yellow-400 hover:to-orange-400 transition-all transform hover:scale-105 shadow-lg"
          >
            <span className="mr-2">🎓</span>
            View Course on Udemy
            <span className="ml-2">→</span>
          </a>
        </div>
      </div>

      {/* Key Benefits */}
      <div className="mb-12">
        <h2 className="text-3xl font-bold mb-8 text-center">
          💎 <span className="gradient-text">Why This Course?</span>
        </h2>
        
        <div className="grid md:grid-cols-3 gap-6">
          <div className="glass-card p-6 text-center hover:bg-white/10 transition-all">
            <div className="w-16 h-16 bg-blue-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">🎯</span>
            </div>
            <h3 className="text-xl font-semibold text-blue-600 mb-2">Job-Ready Skills</h3>
            <p className="text-gray-700">Industry-relevant projects and real-world scenarios that employers value</p>
          </div>

          <div className="glass-card p-6 text-center hover:bg-white/10 transition-all">
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">🛠️</span>
            </div>
            <h3 className="text-xl font-semibold text-green-600 mb-2">Complete Ecosystem</h3>
            <p className="text-gray-700">From Hadoop to modern cloud platforms - everything you need to know</p>
          </div>

          <div className="glass-card p-6 text-center hover:bg-white/10 transition-all">
            <div className="w-16 h-16 bg-purple-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">👥</span>
            </div>
            <h3 className="text-xl font-semibold text-purple-600 mb-2">Beginner Friendly</h3>
            <p className="text-gray-700">No prior experience required - learn Python and Scala from scratch</p>
          </div>
        </div>
      </div>

      {/* What You'll Learn */}
      <div className="mb-12">
        <h2 className="text-3xl font-bold mb-8 text-center">
          🎯 <span className="gradient-text">What You'll Master</span>
        </h2>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { 
              icon: '🏗️', 
              title: 'Big Data Foundations',
              desc: 'Hadoop, HDFS, and Hive fundamentals',
              color: 'blue',
              topics: ['Hadoop Ecosystem', 'HDFS Storage', 'Hive Queries']
            },
            { 
              icon: '⚡', 
              title: 'Spark Mastery',
              desc: 'PySpark and Scala with hands-on projects',
              color: 'orange',
              topics: ['PySpark RDDs', 'DataFrames', 'Spark SQL']
            },
            { 
              icon: '🏢', 
              title: 'Databricks Platform',
              desc: 'Delta Lake, versioning, and optimization',
              color: 'red',
              topics: ['Delta Tables', 'Time Travel', 'Performance']
            },
            { 
              icon: '🌊', 
              title: 'Apache NiFi',
              desc: 'Data flow automation and monitoring',
              color: 'green',
              topics: ['Flow Design', 'Automation', 'Monitoring']
            },
            { 
              icon: '☁️', 
              title: 'AWS Integration',
              desc: 'Glue, Athena, and S3 for cloud analytics',
              color: 'purple',
              topics: ['AWS Glue', 'Athena Queries', 'S3 Storage']
            },
            { 
              icon: '🔧', 
              title: 'Production Skills',
              desc: 'Logging, testing, and error handling',
              color: 'cyan',
              topics: ['Unit Testing', 'Error Handling', 'Best Practices']
            }
          ].map((skill, index) => (
            <div key={index} className="glass-card p-6 hover:bg-white/10 transition-all">
              <div className="flex items-start space-x-4 mb-4">
                <div className={`w-12 h-12 bg-${skill.color}-500/20 rounded-lg flex items-center justify-center flex-shrink-0`}>
                  <span className="text-2xl">{skill.icon}</span>
                </div>
                <div>
                  <h3 className={`text-xl font-semibold text-${skill.color}-600 mb-2`}>{skill.title}</h3>
                  <p className="text-gray-700 text-sm">{skill.desc}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-1">
                {skill.topics.map((topic, topicIndex) => (
                  <span key={topicIndex} className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded-full">
                    {topic}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Tools & Technologies */}
      <div className="mb-12">
        <h2 className="text-3xl font-bold mb-8 text-center">
          🛠️ <span className="gradient-text">Tech Stack You'll Master</span>
        </h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          {[
            { name: 'Hadoop', icon: '🐘', category: 'Big Data' },
            { name: 'Spark', icon: '⚡', category: 'Processing' },
            { name: 'Hive', icon: '🐝', category: 'Warehouse' },
            { name: 'PySpark', icon: '🐍', category: 'Python' },
            { name: 'Scala', icon: '🎯', category: 'Language' },
            { name: 'Databricks', icon: '🧱', category: 'Platform' },
            { name: 'Delta Lake', icon: '🏊', category: 'Storage' },
            { name: 'Apache NiFi', icon: '🌊', category: 'Flow' },
            { name: 'AWS Glue', icon: '☁️', category: 'Cloud' },
            { name: 'Athena', icon: '🔍', category: 'Query' },
            { name: 'PostgreSQL', icon: '🐘', category: 'Database' },
            { name: 'IntelliJ', icon: '💡', category: 'IDE' },
            { name: 'Maven', icon: '📦', category: 'Build' },
            { name: 'PyCharm', icon: '🐍', category: 'IDE' }
          ].map((tech, index) => (
            <div key={index} className="glass-card p-3 text-center hover:bg-white/10 transition-all group">
              <div className="text-2xl mb-2 group-hover:scale-110 transition-transform">{tech.icon}</div>
              <div className="text-sm font-semibold text-blue-600">{tech.name}</div>
              <div className="text-xs text-gray-600">{tech.category}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Who This Is For & Prerequisites */}
      <div className="grid md:grid-cols-2 gap-8 mb-12">
        <div className="glass-card p-6">
          <h3 className="text-2xl font-bold mb-4 flex items-center">
            <span className="mr-3">👥</span>
            <span className="gradient-text">Perfect For</span>
          </h3>
          <div className="space-y-3">
            {[
              { icon: '🚀', text: 'Aspiring Data Engineers' },
              { icon: '👩‍💻', text: 'Software/SQL Developers' },
              { icon: '📊', text: 'Data Analysts & Scientists' },
              { icon: '💼', text: 'Job Interview Preparation' }
            ].map((item, index) => (
              <div key={index} className="flex items-center space-x-3">
                <span className="text-blue-600">{item.icon}</span>
                <span className="text-gray-700">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
        
        <div className="glass-card p-6">
          <h3 className="text-2xl font-bold mb-4 flex items-center">
            <span className="mr-3">📋</span>
            <span className="gradient-text">Prerequisites</span>
          </h3>
          <div className="space-y-3">
            {[
              { icon: '✅', text: 'No programming experience needed', color: 'green' },
              { icon: '✅', text: 'Python & Scala taught from scratch', color: 'green' },
              { icon: '🎯', text: 'Basic SQL knowledge helpful', color: 'blue' },
              { icon: '💡', text: 'Curiosity and dedication', color: 'purple' }
            ].map((item, index) => (
              <div key={index} className="flex items-center space-x-3">
                <span className={`text-${item.color}-600`}>{item.icon}</span>
                <span className="text-gray-700">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Course Outcomes */}
      <div className="mb-12">
        <h2 className="text-3xl font-bold mb-8 text-center">
          🎯 <span className="gradient-text">Career Outcomes</span>
        </h2>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { 
              icon: '🧠', 
              title: 'Master Big Data Concepts',
              desc: 'Understand distributed computing and data architecture',
              gradient: 'from-blue-500 to-cyan-500'
            },
            { 
              icon: '🚀', 
              title: 'Build Production Pipelines',
              desc: 'Deploy Spark and NiFi pipelines on cloud platforms',
              gradient: 'from-purple-500 to-pink-500'
            },
            { 
              icon: '☁️', 
              title: 'Cloud Platform Expertise',
              desc: 'Work confidently with Databricks, AWS Glue, and Delta Lake',
              gradient: 'from-green-500 to-teal-500'
            },
            { 
              icon: '⚙️', 
              title: 'Production Best Practices',
              desc: 'Apply logging, testing, and performance optimization',
              gradient: 'from-orange-500 to-red-500'
            },
            { 
              icon: '🏢', 
              title: 'Enterprise Solutions',
              desc: 'Handle real-world data flow challenges',
              gradient: 'from-indigo-500 to-purple-500'
            },
            { 
              icon: '💼', 
              title: 'Job Readiness',
              desc: 'Gain practical experience for Data Engineer roles',
              gradient: 'from-pink-500 to-rose-500'
            }
          ].map((outcome, index) => (
            <div key={index} className="glass-card p-6 hover:bg-white/10 transition-all group">
              <div className={`w-12 h-12 bg-gradient-to-r ${outcome.gradient} rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                <span className="text-2xl">{outcome.icon}</span>
              </div>
              <h3 className="text-lg font-semibold text-blue-600 mb-2">{outcome.title}</h3>
              <p className="text-gray-700 text-sm">{outcome.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Final CTA */}
      <div className="text-center bg-gradient-to-r from-green-500/20 to-blue-600/20 rounded-2xl p-8">
        <h3 className="text-2xl font-bold mb-4">🚀 Launch Your Data Engineering Career!</h3>
        <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
          Join the ranks of successful Data Engineers. Master the complete big data ecosystem 
          with hands-on projects and real-world experience.
        </p>
        <a
          href={udemyLink}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center bg-gradient-to-r from-green-500 to-blue-600 text-white font-bold px-8 py-4 rounded-lg hover:from-green-400 hover:to-blue-500 transition-all transform hover:scale-105 shadow-lg"
        >
          <span className="mr-2">🎓</span>
          Start Your Journey
          <span className="ml-2">→</span>
        </a>
      </div>
    </div>
    </>
  );
};

export default CourseDataEng;